import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useCountUp } from '@/hooks/useCountUp';
import { useLiveData } from '@/hooks/useLiveData';

const partnerTypes = [
  { icon: 'ri-bank-line', title: '机构伙伴', desc: '与全球顶级投行、券商、期货公司建立深度合作，共享交易通道、研究资源与市场基础设施。', count: 42 },
  { icon: 'ri-node-tree', title: '渠道合作方', desc: '联合私人银行、基金代销平台与第三方财富管理机构，为终端客户提供涵盖公募基金FOF、私募基金组合与结构化产品的全品类资产管理配置服务。', count: 28 },
  { icon: 'ri-user-star-line', title: '投资顾问', desc: '与独立投资顾问（IFA）及家族办公室顾问建立协同网络，共同服务高净值客户群体。', count: 65 },
  { icon: 'ri-home-heart-line', title: '家族办公室', desc: '与亚太区领先的联合家族办公室（MFO）及独立家办建立战略合作，在量化策略配置、跨境资产管理与家族信托架构等领域实现资源共享与优势互补。', count: 18 },
  { icon: 'ri-cpu-line', title: '金融科技提供商', desc: '携手全球领先的金融科技企业，共同研发量化交易系统、智能风控平台与数据解决方案。', count: 35 },
  { icon: 'ri-building-4-line', title: '企业客户', desc: '为上市公司、大型企业集团提供定制化的现金管理、外汇对冲与跨境资本运作方案。', count: 80 },
];

const processSteps = [
  { step: '01', title: '初步沟通', desc: '了解合作需求与业务场景', icon: 'ri-chat-1-line' },
  { step: '02', title: '需求评估', desc: '分析合作可行性与合规要求', icon: 'ri-search-eye-line' },
  { step: '03', title: '方案设计', desc: '量身定制合作方案与服务标准', icon: 'ri-draft-line' },
  { step: '04', title: '合规审查', desc: '法律合规团队进行全面审查', icon: 'ri-shield-check-line' },
  { step: '05', title: '正式上线', desc: '签署协议，完成对接与培训', icon: 'ri-rocket-line' },
  { step: '06', title: '持续服务', desc: '定期回顾，持续优化服务方案', icon: 'ri-refresh-line' },
];

const brokerPartners = [
  {
    name: 'GTCFX',
    chineseName: 'GTC环球金服',
    country: '阿联酋',
    flag: '🇦🇪',
    years: '15-20年',
    rating: 9.23,
    tradingEnv: 'AAA',
    mtStatus: '主标MT4',
    accountType: 'ECN账户',
    regulations: [
      { flag: '🇬🇧', name: '英国FCA', license: '全牌照 (MM)' },
      { flag: '🇦🇺', name: '澳大利亚ASIC', license: 'STP牌照' },
      { flag: '🇦🇪', name: '阿联酋CMA', license: '衍生品 (EP)' },
    ],
    website: 'www.gtcfx.com',
    images: [
      'https://storage.readdy-site.link/project_files/9dc21385-3279-4aa9-82a8-4dec967f9595/135b31c2-5c94-4f32-a7e1-866356a687c9_compressed_2.webp',
      'https://storage.readdy-site.link/project_files/9dc21385-3279-4aa9-82a8-4dec967f9595/906c272a-5922-4e1d-9226-4d6d1ac23c85_compressed_1.webp',
    ],
  },
  {
    name: 'MEGA FUSION',
    chineseName: '安汇',
    country: '澳大利亚',
    flag: '🇦🇺',
    years: '5-10年',
    rating: 8.73,
    tradingEnv: '',
    mtStatus: '主标MT4',
    accountType: 'ECN账户',
    regulations: [
      { flag: '🇦🇺', name: '澳大利亚ASIC', license: '机构外汇直通 (STP)' },
      { flag: '🇿🇦', name: '南非FSCA', license: '衍生品 (EP)' },
    ],
    website: 'www.mega-fusion.com',
    images: [
      'https://storage.readdy-site.link/project_files/9dc21385-3279-4aa9-82a8-4dec967f9595/2207491c-5b2f-4b06-8e54-86b8e0d4ac0b_compressed_3.webp',
      'https://storage.readdy-site.link/project_files/9dc21385-3279-4aa9-82a8-4dec967f9595/8462347f-3c31-4c28-a9e0-4c6d0e2102c6_compressed_4.webp',
    ],
  },
  {
    name: 'AEFOREX',
    chineseName: '极星金融',
    country: '澳大利亚',
    flag: '🇦🇺',
    years: '5-10年',
    rating: 6.12,
    tradingEnv: '',
    mtStatus: '主标MT4',
    accountType: '黄金 / 原油账户',
    regulations: [
      { flag: '🇦🇺', name: '澳大利亚ASIC', license: '机构衍生品 (MM)', detail: '牌照号 246250 · AURORA ENERGY PTY LIMITED' },
    ],
    website: 'aeforex.com',
    images: [
      'https://storage.readdy-site.link/project_files/9dc21385-3279-4aa9-82a8-4dec967f9595/ec19503c-3be4-437b-947e-953e5866aadd_compressed_5.webp',
      'https://storage.readdy-site.link/project_files/9dc21385-3279-4aa9-82a8-4dec967f9595/7acb6c5c-71ec-40db-a83e-b12944a704bf_compressed_6.webp',
    ],
  },
];

/* ── Live Partner Stats Bar ── */
function PartnerStatsBar() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const { formatted: totalFormatted } = useCountUp({ target: 268, duration: 2200, suffix: '+', format: 'compact', enabled: visible });
  const { formatted: liveFormatted, flash } = useLiveData({ initial: 271.4, range: 3, decimals: 1, interval: 3500 });
  const { formatted: regionFormatted } = useCountUp({ target: 15, duration: 1800, suffix: '', format: 'compact', enabled: visible });

  return (
    <div ref={ref} className="terminal-module p-4 md:p-5 mb-10">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-red-400/40"></span>
          <span className="w-2 h-2 rounded-full bg-accent-400/40"></span>
          <span className="w-2 h-2 rounded-full bg-emerald-400/40"></span>
          <span className="text-[9px] mono-data text-foreground-600 tracking-wider ml-1">PARTNER_ECOSYSTEM :: LIVE</span>
        </div>
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow"></span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 text-center">
        <div>
          <span className="block text-[9px] mono-data text-foreground-600 tracking-wide mb-1">TOTAL PARTNERS</span>
          <span className="block text-2xl md:text-3xl mono-data font-bold text-foreground-100 tabular-nums">{visible ? totalFormatted : '--'}</span>
        </div>
        <div>
          <span className="block text-[9px] mono-data text-foreground-600 tracking-wide mb-1">实时在线</span>
          <span className={`block text-2xl md:text-3xl mono-data font-bold tabular-nums transition-all duration-300 ${flash ? 'text-accent-400 data-tick' : 'text-emerald-400'}`}>
            {liveFormatted}
          </span>
        </div>
        <div>
          <span className="block text-[9px] mono-data text-foreground-600 tracking-wide mb-1">覆盖区域</span>
          <span className="block text-2xl md:text-3xl mono-data font-bold text-foreground-100 tabular-nums">{visible ? regionFormatted : '--'}</span>
        </div>
        <div>
          <span className="block text-[9px] mono-data text-foreground-600 tracking-wide mb-1">STATUS</span>
          <span className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full bg-emerald-400/10 border border-emerald-400/20">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow"></span>
            <span className="text-[10px] mono-data font-bold text-emerald-400">ACTIVE</span>
          </span>
        </div>
      </div>
    </div>
  );
}

/* ── Process Step Card with Animation ── */
function ProcessStepCard({ step, index }: { step: typeof processSteps[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [activated, setActivated] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          const timer = setTimeout(() => setActivated(true), index * 200);
          return () => clearTimeout(timer);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [index]);

  return (
    <div ref={ref} className="relative">
      <div className={`bg-background-100 rounded-xl border p-5 text-center transition-all duration-500 ${
        activated
          ? 'border-accent-500/40 -translate-y-1 shadow-[0_0_20px_rgba(200,160,80,0.06)]'
          : 'border-background-300/30'
      } ${activated && index === 0 ? 'process-step-active' : ''}`}>
        <span className={`mono-data text-2xl font-bold transition-all duration-500 ${
          activated ? 'text-accent-500/40' : 'text-primary-500/25'
        }`}>{step.step}</span>
        <div className={`w-9 h-9 mx-auto my-2.5 rounded-lg border flex items-center justify-center transition-all duration-500 ${
          activated ? 'bg-accent-500/10 border-accent-500/25' : 'bg-primary-500/10 border-primary-500/15'
        }`}>
          <i className={`${step.icon} text-base transition-colors duration-500 ${
            activated ? 'text-accent-400' : 'text-primary-400'
          }`}></i>
        </div>
        <h4 className="text-sm font-semibold text-foreground-200 mt-1 mb-1">{step.title}</h4>
        <p className="text-xs text-foreground-500 leading-relaxed font-light">{step.desc}</p>
      </div>
      {index < processSteps.length - 1 && (
        <div className="hidden lg:block absolute top-1/2 -right-3 -translate-y-1/2 z-10">
          <i className={`ri-arrow-right-s-line transition-all duration-500 ${
            activated ? 'text-accent-400/60' : 'text-foreground-600/30'
          }`}></i>
        </div>
      )}
    </div>
  );
}

/* ── Broker Partner Card with 3D tilt + WikiFX Credentials ── */
function BrokerPartnerCard({ broker }: { broker: typeof brokerPartners[0] }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [expandedImg, setExpandedImg] = useState<string | null>(null);
  const [tilt, setTilt] = useState({ rotateX: 0, rotateY: 0 });
  const [glowPos, setGlowPos] = useState({ x: 50, y: 50 });
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (wrapperRef.current) obs.observe(wrapperRef.current);
    return () => obs.disconnect();
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const rotateY = ((x - cx) / cx) * 8;
    const rotateX = -((y - cy) / cy) * 6;
    setTilt({ rotateX, rotateY });
    setGlowPos({ x: (x / rect.width) * 100, y: (y / rect.height) * 100 });
  };

  const handleMouseLeave = () => {
    setTilt({ rotateX: 0, rotateY: 0 });
    setGlowPos({ x: 50, y: 50 });
    setHovered(false);
  };

  const websiteUrl = `https://${broker.website}`;

  return (
    <>
      <div
        ref={wrapperRef}
        style={{ perspective: '1000px' }}
        className="relative"
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={handleMouseLeave}
      >
        {/* 3D card */}
        <div
          ref={cardRef}
          style={{
            transform: hovered
              ? `rotateX(${tilt.rotateX}deg) rotateY(${tilt.rotateY}deg) translateZ(6px)`
              : 'rotateX(0deg) rotateY(0deg) translateZ(0px)',
            transition: hovered ? 'transform 0.08s ease-out' : 'transform 0.5s cubic-bezier(0.23,1,0.32,1)',
          }}
          className={`relative bg-background-100 rounded-xl border overflow-hidden ${
            visible
              ? hovered
                ? 'border-accent-500/50 shadow-[0_20px_60px_rgba(200,160,80,0.15),0_0_30px_rgba(200,160,80,0.08)]'
                : 'border-accent-500/25 shadow-[0_4px_24px_rgba(200,160,80,0.06)]'
              : 'border-background-300/30'
          } transition-shadow duration-300`}
        >
          {/* Mouse-follow shimmer layer */}
          <div
            className="pointer-events-none absolute inset-0 rounded-xl transition-opacity duration-300"
            style={{
              opacity: hovered ? 1 : 0,
              background: `radial-gradient(circle 220px at ${glowPos.x}% ${glowPos.y}%, rgba(200,160,80,0.10) 0%, transparent 70%)`,
            }}
          />

          {/* Header */}
          <div className="p-5 md:p-6 relative z-10">
            <div className="flex items-center justify-between mb-3">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-400/10 border border-emerald-400/20">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow"></span>
                <span className="text-[10px] mono-data font-bold text-emerald-400">监管合规认证</span>
              </span>
              <span className="text-[10px] mono-data text-foreground-600">WikiFX 权威背书</span>
            </div>

            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl leading-none">{broker.flag}</span>
              <span className="text-xs text-foreground-500">{broker.country} · {broker.years}</span>
            </div>
            <h3
              className="text-lg font-heading font-bold transition-colors duration-300"
              style={{ color: hovered ? 'oklch(var(--accent-400))' : 'oklch(var(--foreground-100))' }}
            >
              {broker.name}
            </h3>
            <p className="text-xs text-foreground-500 mt-0.5">{broker.chineseName}</p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-2.5 mt-4">
              <div className="bg-background-50 rounded-lg p-3 text-center border border-background-300/20">
                <p className="text-[9px] mono-data text-foreground-600 mb-1 tracking-wide">天眼评分</p>
                <p className="text-2xl font-bold text-accent-400 tabular-nums leading-none">{broker.rating}</p>
                <p className="text-[10px] text-foreground-500 mt-0.5">/10</p>
              </div>
              <div className="bg-background-50 rounded-lg p-3 text-center border border-background-300/20">
                <p className="text-[9px] mono-data text-foreground-600 mb-1 tracking-wide">交易环境</p>
                <p className="text-2xl font-bold text-foreground-100 tabular-nums leading-none">{broker.tradingEnv || '--'}</p>
              </div>
              <div className="bg-background-50 rounded-lg p-3 text-center border border-background-300/20">
                <p className="text-[9px] mono-data text-foreground-600 mb-1 tracking-wide">MT4/5</p>
                <p className="text-sm font-bold text-foreground-100 leading-tight mt-1">{broker.mtStatus}</p>
              </div>
            </div>

            {/* Account type */}
            <div className="flex items-center gap-2 mt-3">
              <span className="px-2.5 py-1 rounded-md bg-primary-500/8 border border-primary-500/15 text-[10px] mono-data text-primary-400">
                {broker.accountType}
              </span>
              <span className="px-2.5 py-1 rounded-md bg-accent-500/8 border border-accent-500/15 text-[10px] mono-data text-accent-400">
                ECN直通
              </span>
            </div>
          </div>

          {/* Images */}
          <div className="px-5 md:px-6 pb-4 relative z-10">
            <p className="text-[9px] mono-data text-foreground-600 mb-2 tracking-wider uppercase">WikiFX 资质凭证</p>
            <div className="grid grid-cols-2 gap-3">
              {broker.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setExpandedImg(img)}
                  className="relative rounded-lg border border-background-300/40 overflow-hidden bg-background-50 cursor-pointer group hover:border-accent-500/40 transition-colors duration-300"
                >
                  <img
                    src={img}
                    alt={`${broker.name} WikiFX 资质凭证 ${i + 1}`}
                    className="w-full h-auto max-h-[260px] object-contain object-top"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors duration-300 flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 px-2.5 py-1 rounded-full bg-background-950/70 text-[10px] text-background-50 whitespace-nowrap">
                      <i className="ri-zoom-in-line mr-1"></i>点击放大
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Regulations */}
          <div className="px-5 md:px-6 pb-4 relative z-10">
            <p className="text-[9px] mono-data text-foreground-600 mb-2.5 tracking-wider uppercase">持牌监管</p>
            <div className="space-y-2">
              {broker.regulations.map((reg, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <span className="text-base leading-none mt-0.5 shrink-0">{reg.flag}</span>
                  <div className="min-w-0">
                    <span className="text-foreground-300">{reg.name}</span>
                    <span className="text-accent-400 ml-1.5">{reg.license}</span>
                    {reg.detail && (
                      <p className="text-[10px] text-foreground-600 mt-0.5 truncate">{reg.detail}</p>
                    )}
                  </div>
                  <span className="ml-auto shrink-0 px-1.5 py-0.5 rounded bg-emerald-400/10 border border-emerald-400/15 text-[9px] mono-data text-emerald-400 whitespace-nowrap">监管中</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA: Visit Website */}
          <div className="px-5 md:px-6 pb-5 md:pb-6 relative z-10">
            <div className="pt-3 border-t border-background-300/30">
              <a
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="group/btn flex items-center justify-between w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/30 hover:border-accent-500/40 hover:bg-accent-500/5 transition-all duration-300 cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-accent-500/10 flex items-center justify-center">
                    <i className="ri-global-line text-[11px] text-accent-500"></i>
                  </div>
                  <span className="text-[11px] mono-data text-foreground-400 group-hover/btn:text-accent-400 transition-colors duration-300">
                    {broker.website}
                  </span>
                </div>
                <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent-500/10 border border-accent-500/20 text-[10px] mono-data font-bold text-accent-400 group-hover/btn:bg-accent-500/20 transition-colors duration-300 whitespace-nowrap">
                  访问官网
                  <i className="ri-arrow-right-up-line text-xs group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform duration-300"></i>
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {expandedImg && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
          onClick={() => setExpandedImg(null)}
        >
          <div className="relative max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setExpandedImg(null)}
              className="absolute -top-10 right-0 w-8 h-8 rounded-full bg-background-950/60 flex items-center justify-center text-background-50 hover:bg-background-950/80 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
            <img
              src={expandedImg}
              alt="WikiFX 资质凭证"
              className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </>
  );
}

/* ── Trust Badge Card with scroll reveal ── */
function TrustBadgeCard({
  index,
  icon,
  stat,
  statLabel,
  title,
  desc,
}: {
  index: number;
  icon: string;
  stat: string;
  statLabel: string;
  title: string;
  desc: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          const timer = setTimeout(() => setVisible(true), index * 120);
          return () => clearTimeout(timer);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [index]);

  return (
    <div
      ref={ref}
      className={`relative bg-background-50 rounded-xl border p-6 md:p-7 overflow-hidden transition-all duration-700 cursor-default ${
        visible
          ? 'opacity-100 translate-y-0 border-accent-500/20 shadow-[0_4px_24px_rgba(200,160,80,0.05)]'
          : 'opacity-0 translate-y-6 border-background-300/20'
      } hover:border-accent-500/40 hover:shadow-[0_8px_40px_rgba(200,160,80,0.10)] hover:-translate-y-1`}
    >
      {/* corner accent */}
      <div className="absolute top-0 right-0 w-20 h-20 bg-accent-500/3 rounded-bl-[80px] pointer-events-none" />

      {/* icon */}
      <div className="w-12 h-12 rounded-xl bg-accent-500/10 border border-accent-500/15 flex items-center justify-center mb-4 transition-colors duration-300 group-hover:bg-accent-500/20">
        <i className={icon + ' text-xl text-accent-400'}></i>
      </div>

      {/* stat */}
      <div className="flex items-baseline gap-1.5 mb-2">
        <span className="text-2xl md:text-3xl font-bold text-accent-400 tabular-nums leading-none">{stat}</span>
        <span className="text-[10px] mono-data text-foreground-600 tracking-wide">{statLabel}</span>
      </div>

      {/* title */}
      <h4 className="text-sm font-heading font-bold text-foreground-200 mb-2">{title}</h4>

      {/* desc */}
      <p className="text-xs text-foreground-500 leading-relaxed font-light">{desc}</p>

      {/* bottom accent line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent-500/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500" />
    </div>
  );
}

export default function Cooperation() {
  return (
    <div className="min-h-screen bg-background-50">
      {/* Page Header */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-background-50 overflow-hidden">
        <div className="absolute inset-0 bg-dot-matrix opacity-25"></div>
        <div className="relative px-6 md:px-12 lg:px-20">
          <div className="max-w-[1400px] mx-auto">
            <p className="text-accent-500 text-xs md:text-sm font-semibold tracking-[0.25em] uppercase mb-4">Cooperation Platform</p>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-100 leading-tight">合作平台</h1>
            <p className="mt-4 text-sm md:text-base text-foreground-400 max-w-2xl leading-relaxed font-light">
              开放共赢，共建量化驱动资产管理合作生态。面向家族办公室、基金代销渠道与机构投资者，提供一站式资产配置与策略服务。
            </p>
          </div>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-20 md:py-28 bg-background-100">
        <div className="px-6 md:px-12 lg:px-20">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-10">
              <p className="text-xs md:text-sm font-semibold tracking-[0.25em] uppercase text-accent-500 mb-3">Partner Ecosystem</p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-100">合作生态</h2>
            </div>

            <PartnerStatsBar />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {partnerTypes.map((p) => (
                <PartnerCard key={p.title} partner={p} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Broker Partners */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="px-6 md:px-12 lg:px-20">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-14">
              <p className="text-xs md:text-sm font-semibold tracking-[0.25em] uppercase text-accent-500 mb-3">Licensed Broker Partners</p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-100">战略合作券商</h2>
              <p className="mt-3 text-sm text-foreground-400 max-w-2xl mx-auto leading-relaxed font-light">
                携手 WikiFX 权威认证持牌券商，多重监管牌照覆盖英国、澳大利亚、阿联酋、南非等全球主要金融中心，为客户提供安全合规的交易基础设施。
              </p>
            </div>

            {/* Terminal-style sub-header */}
            <div className="terminal-module p-4 md:p-5 mb-10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-400/40"></span>
                  <span className="w-2 h-2 rounded-full bg-accent-400/40"></span>
                  <span className="w-2 h-2 rounded-full bg-emerald-400/40"></span>
                  <span className="text-[9px] mono-data text-foreground-600 tracking-wider ml-1">BROKER_CREDENTIALS :: VERIFIED</span>
                </div>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow"></span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 text-center">
                <div>
                  <span className="block text-[9px] mono-data text-foreground-600 tracking-wide mb-1">合作券商</span>
                  <span className="block text-2xl md:text-3xl mono-data font-bold text-foreground-100 tabular-nums">3</span>
                </div>
                <div>
                  <span className="block text-[9px] mono-data text-foreground-600 tracking-wide mb-1">平均评分</span>
                  <span className="block text-2xl md:text-3xl mono-data font-bold text-accent-400 tabular-nums">8.03</span>
                </div>
                <div>
                  <span className="block text-[9px] mono-data text-foreground-600 tracking-wide mb-1">监管牌照</span>
                  <span className="block text-2xl md:text-3xl mono-data font-bold text-foreground-100 tabular-nums">6+</span>
                </div>
                <div>
                  <span className="block text-[9px] mono-data text-foreground-600 tracking-wide mb-1">STATUS</span>
                  <span className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full bg-emerald-400/10 border border-emerald-400/20">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow"></span>
                    <span className="text-[10px] mono-data font-bold text-emerald-400">VERIFIED</span>
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
              {brokerPartners.map((broker) => (
                <BrokerPartnerCard key={broker.name} broker={broker} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Compliance Foundation */}
      <section className="py-20 md:py-28 bg-background-100">
        <div className="px-6 md:px-12 lg:px-20">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-14">
              <p className="text-xs md:text-sm font-semibold tracking-[0.25em] uppercase text-accent-500 mb-3">Trust Foundation</p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-100">信任基石</h2>
              <p className="mt-3 text-sm text-foreground-400 max-w-2xl mx-auto leading-relaxed font-light">
                全球顶级监管背书、银行级资金安全、军事级数据加密，为您的每一笔交易保驾护航。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              <TrustBadgeCard
                index={0}
                icon="ri-shield-check-fill"
                stat="6+"
                statLabel="监管牌照"
                title="多重监管认证"
                desc="受英国FCA、澳大利亚ASIC、阿联酋CMA等全球顶级金融监管机构全面监管，持牌合规运营。"
              />
              <TrustBadgeCard
                index={1}
                icon="ri-safe-2-fill"
                stat="100%"
                statLabel="隔离存管"
                title="资金安全保障"
                desc="客户资金独立存管于Barclays、Commonwealth Bank等顶级银行隔离账户，与公司运营资金完全分离。"
              />
              <TrustBadgeCard
                index={2}
                icon="ri-lock-password-fill"
                stat="AES-256"
                statLabel="加密标准"
                title="数据安全加密"
                desc="全站TLS 1.3传输加密，核心数据AES-256静态加密，通过ISO 27001信息安全管理体系认证。"
              />
              <TrustBadgeCard
                index={3}
                icon="ri-server-fill"
                stat="99.99%"
                statLabel="SLA保障"
                title="系统高可用性"
                desc="全球多活数据中心架构，毫秒级故障自动切换，全年99.99%服务可用性承诺。"
              />
              <TrustBadgeCard
                index={4}
                icon="ri-user-search-fill"
                stat="7×24"
                statLabel="风控监控"
                title="合规风控体系"
                desc="完善的KYC/AML全流程合规体系，7×24小时智能风控监控，可疑交易实时预警拦截。"
              />
              <TrustBadgeCard
                index={5}
                icon="ri-file-shield-fill"
                stat="GDPR"
                statLabel="隐私合规"
                title="隐私保护合规"
                desc="严格遵守GDPR欧盟数据保护条例及中国个人信息保护法，数据不出境，隐私零泄露。"
              />
            </div>

            {/* Trust chain decoration */}
            <div className="mt-14 flex items-center justify-center gap-3 md:gap-5 flex-wrap">
              {[
                { icon: 'ri-bank-fill', label: '银行级存管' },
                { icon: 'ri-shield-star-fill', label: 'SSL加密传输' },
                { icon: 'ri-verified-badge-fill', label: '持牌合规' },
                { icon: 'ri-umbrella-fill', label: '负余额保护' },
                { icon: 'ri-shield-flash-fill', label: 'DDoS防护' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-50 border border-background-300/30">
                  <i className={`${item.icon} text-sm text-accent-400`}></i>
                  <span className="text-[11px] mono-data text-foreground-500">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Cooperation Process */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="px-6 md:px-12 lg:px-20">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-14">
              <p className="text-xs md:text-sm font-semibold tracking-[0.25em] uppercase text-accent-500 mb-3">Cooperation Process</p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-100">合作流程</h2>
              <p className="mt-3 text-[10px] mono-data text-foreground-600">
                <span className="text-emerald-400/70">PIPELINE</span>
                <span className="mx-2 text-foreground-800">::</span>
                <span className="text-foreground-500">AVG ONBOARDING  </span>
                <span className="text-accent-400/80">14 DAYS</span>
                <span className="mx-2 text-foreground-800">|</span>
                <span className="text-foreground-500">SUCCESS RATE  </span>
                <span className="text-primary-400/80">94.6%</span>
              </p>
            </div>

            {/* Timeline style process */}
            <div className="relative">
              {/* Connecting line */}
              <div className="hidden lg:block absolute top-1/2 left-[5%] right-[5%] h-px bg-gradient-to-r from-primary-400/20 via-accent-400/30 to-primary-400/20 -translate-y-1/2"></div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                {processSteps.map((step, idx) => (
                  <ProcessStepCard key={step.step} step={step} index={idx} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-32 overflow-hidden bg-background-50">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Hong%20Kong%20skyline%20at%20blue%20hour%20dusk%2C%20Victoria%20Harbour%2C%20dramatic%20dark%20navy%20sky%20with%20golden%20horizon%2C%20modern%20financial%20district%20buildings%2C%20cinematic%20atmosphere%2C%20deep%20shadows%2C%20moody%20and%20powerful%2C%20professional%20tone%2C%20no%20text%2C%20no%20people&width=1920&height=800&seq=coop-cta-hk-dark-2026&orientation=landscape"
            alt="香港黄昏天际线"
            className="w-full h-full object-cover object-top opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/75 to-background-50/30"></div>
        </div>
        <div className="relative px-6 md:px-12 lg:px-20 text-center">
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-100 leading-tight mb-5">
            开放合作平台
          </h2>
          <p className="text-sm md:text-base text-foreground-400 max-w-2xl mx-auto leading-relaxed mb-10 font-light">
            面向家族办公室与基金渠道合作伙伴，提供量化策略输出、资产管理与跨境配置的全方位合作方案。我们期待与您携手，共建量化驱动资管新生态。
          </p>
          <Link
            to="/contact"
            className="group inline-flex items-center gap-2.5 px-8 py-3.5 bg-accent-500 text-background-950 font-bold text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap shadow-[0_0_25px_rgba(200,160,80,0.12)] hover:shadow-[0_0_35px_rgba(200,160,80,0.2)]"
          >
            <i className="ri-mail-send-line text-lg"></i>
            发起合作沟通
            <i className="ri-arrow-right-up-line group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300"></i>
          </Link>
        </div>
      </section>
    </div>
  );
}

/* ── Partner Card with Live Count ── */
function PartnerCard({ partner }: { partner: typeof partnerTypes[0] }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const { formatted } = useCountUp({ target: partner.count, duration: 1500, suffix: '+', format: 'compact', enabled: visible });

  return (
    <div ref={ref} className="data-panel p-6 md:p-7 group hover:-translate-y-1 transition-all duration-300">
      <div className="w-11 h-11 rounded-lg bg-primary-500/10 border border-primary-500/15 flex items-center justify-center mb-4 group-hover:border-accent-500/30 transition-colors duration-300">
        <i className={`${partner.icon} text-lg text-primary-400 group-hover:text-accent-400 transition-colors duration-300`}></i>
      </div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-base font-heading font-bold text-foreground-100">{partner.title}</h3>
        <span className="text-xs mono-data font-bold text-accent-400 tabular-nums">
          {visible ? formatted : '--'}
        </span>
      </div>
      <p className="text-sm text-foreground-500 leading-relaxed font-light">{partner.desc}</p>
      <div className="mt-3 pt-3 border-t border-background-300/30 flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow"></span>
        <span className="text-[9px] mono-data text-foreground-600">ACTIVE PARTNERS</span>
        <span className="text-[10px] mono-data font-bold text-emerald-400 tabular-nums ml-auto">
          {Math.floor(partner.count * 0.85)}+
        </span>
      </div>
    </div>
  );
}